/* ============================================================================
   cae-extra-events.js — additional storefront interaction tracking for CAE.

   ISOLATION PRINCIPLE: this file owns NO state of its own beyond its listeners.
   It reads the DOM + window globals and emits through the existing CAE client
   via window.CAEDev.emit(name, detail). It does NOT edit cae-client.js, the
   scarcity engine, or any other upstream system. emit() in cae-client.js
   auto-stamps eventId / eventName / createdAt / sessionId / viewerId(vid) / page
   and the pulse wrapper adds device / utm / entry / country / page_type — so the
   `detail` object here carries ONLY the event-specific fields.

   Destination is the same as every other CAE event: the GCP analytics-ingest
   Cloud Run service (/cae/event) -> Pub/Sub cae-events -> BigQuery
   fullstack-hub.cae_customer_analytics.events (read via events_dedup).

   Events emitted:
     home_element_click   home CTA / banner-slide / collection-card click
     slider_click         main slideshow dot / next-section arrow (user-initiated)
     nav_click            header + mobile-drawer + footer + social link click
     search_performed     predictive-search header form submit
     pdp_tab_click        PDP accordion (details/sizing/specs/returns/...) open/close — summary-click based
     size_chart_opened    PDP "Size chart" link
     video_play           native <video> first play (milestone=start)
     form_submitted       any same-document lead/contact/account form submit
     review_interaction   Judge.me reviews widget — write/paginate/sort/filter/helpful/badge
     (review_seen         STOPPED 2026-09-23 - the widget's impression, once a page, said
                          nothing the page_view did not; rows before that date remain)
     easy_suggestion_add  Easy Suggestion cross-sell card checked (added)
     easy_suggestion_delete  Easy Suggestion cross-sell card unchecked (removed)
     reco_state            once/page: which recommendation state was served
                          (source, count, top handles, hash, age, colour, flow)

   Loaded defer AFTER cae-client.js in layout/theme.liquid, so window.CAEDev
   exists by the time this runs (with a short ready-wait fallback just in case).
   ============================================================================ */
(function () {
    'use strict';

    if (window.CAE_DISABLED === true) return; // mirror cae-client's emergency kill

    /* ---- emit bridge: queue until CAEDev (cae-client.js) is ready ---------- */
    var _pending = [];
    var _ready = false;
    // Events that fire right as the shopper LEAVES the page: home CTA/slide -> off-site,
    // video -> navigate. For these the 15s batch + unload-beacon can drop the row --
    // especially on an EXTERNAL navigation (the persisted localStorage queue can't
    // re-send from another origin) or on a preview with ?_clear_storage=1 (which wipes
    // it on return). So force an immediate send right after emitting; cae-client's
    // flushNow() uses a keepalive fetch that survives the navigation.
    var FLUSH_AFTER = { home_element_click: 1, video_play: 1 };
    function emit(name, detail) {
        // Preview-only F12 console log, gated by the panel's "Storefront interactions"
        // category (window.AGB_consoleCat('interaction'); prefix [cae-extra] is mapped
        // to the 'interaction' category in theme.liquid's PREFIX_CAT). Never prints on
        // live (AGB_consoleCat is false off-preview). Independent of the [cae] line
        // cae-client emits under the 'cae_event' category.
        try { if (window.AGB_consoleCat && window.AGB_consoleCat('interaction')) console.log('[cae-extra]', name, detail || {}); } catch (e) {}
        try {
            if (_ready && window.CAEDev && typeof window.CAEDev.emit === 'function') {
                var r = window.CAEDev.emit(name, detail || {});
                if (FLUSH_AFTER[name]) { try { if (typeof window.CAEDev.flushNow === 'function') window.CAEDev.flushNow(); } catch (e2) {} }
                return r;
            }
            _pending.push([name, detail || {}]);
        } catch (e) {}
        return null;
    }
    function _drain() {
        try {
            if (!(window.CAEDev && typeof window.CAEDev.emit === 'function')) return false;
            _ready = true;
            for (var i = 0; i < _pending.length; i++) {
                try { window.CAEDev.emit(_pending[i][0], _pending[i][1]); } catch (e) {}
            }
            _pending.length = 0;
            return true;
        } catch (e) { return false; }
    }
    function _waitForCae(attempt) {
        if (_drain()) return;
        attempt = attempt || 0;
        if (attempt > 50) return; // ~5s; give up quietly (events already collected pre-ready are flushed on drain)
        setTimeout(function () { _waitForCae(attempt + 1); }, 100);
    }

    /* ---- small DOM helpers ------------------------------------------------- */
    function text(el) {
        try {
            var t = (el && (el.textContent || '')).replace(/\s+/g, ' ').trim();
            return t ? t.slice(0, 200) : null;
        } catch (e) { return null; }
    }
    function attr(el, name) { try { return el && el.getAttribute ? el.getAttribute(name) : null; } catch (e) { return null; } }
    function isHome() {
        try { var p = location.pathname || ''; return p === '/' || p === ''; } catch (e) { return false; }
    }
    function productHandle() {
        try { var m = (location.pathname || '').match(/\/products\/([^\/\?#]+)/); return m ? decodeURIComponent(m[1]) : null; }
        catch (e) { return null; }
    }
    function isExternal(href) {
        try {
            if (!href || /^(#|\/|mailto:|tel:|javascript:)/i.test(href)) return false;
            if (!/^https?:\/\//i.test(href)) return false;
            return new URL(href, location.href).host !== location.host;
        } catch (e) { return false; }
    }
    // Stable Shopify section wrapper: <section id="shopify-section-<KEY>" class="… shopify-section--<TYPE>">
    function sectionInfo(el) {
        try {
            var sec = el && el.closest ? el.closest('[id^="shopify-section-"]') : null;
            if (!sec) return { id: null, type: null, el: null };
            var m = (sec.className || '').match(/shopify-section--([\w-]+)/);
            return { id: sec.id || null, type: m ? m[1] : null, el: sec };
        } catch (e) { return { id: null, type: null, el: null }; }
    }
    // 1-based DOM index of `node` among `selector` matches within `root`.
    function indexWithin(root, selector, node) {
        try {
            if (!root || !node) return null;
            var all = root.querySelectorAll(selector);
            for (var i = 0; i < all.length; i++) { if (all[i] === node) return i + 1; }
        } catch (e) {}
        return null;
    }

    /* ===========================================================================
       1) home_element_click — home CTAs / banner slides / collection cards
       (featured products already emit card_click via cae-client.js — excluded)
       =========================================================================== */
    function installHomeClickDelegate() {
        document.addEventListener('click', function (e) {
            try {
                if (!isHome()) return;
                var t = e.target;
                if (!t || t.nodeType !== 1 || !t.closest) return;
                var a = t.closest('a[href]');
                if (!a) return;
                // Already covered by card_click — don't double-count featured products.
                if (a.closest('product-card, .product-card')) return;
                var sec = sectionInfo(a);
                if (!sec.el) return; // must be inside a home section wrapper

                var elementType = null;
                if (a.matches('.slideshow__slide')) elementType = 'slide';
                else if (a.matches('.collection-card')) elementType = 'collection_card';
                else return; // other anchors (handled by nav_click / card_click or not requested)

                var href = attr(a, 'href');
                emit('home_element_click', {
                    element_type: elementType,
                    label:        text(a),                      // null when label is baked into a banner image
                    href:         href,
                    section_id:   sec.id,
                    section_type: sec.type,
                    position:     indexWithin(sec.el, elementType === 'slide' ? '.slideshow__slide' : '.collection-card', a),
                    media_type:   elementType === 'slide' ? attr(a, 'media-type') : null,
                    is_external:  isExternal(href)
                });
            } catch (err) {}
        }, true);
    }

    /* ===========================================================================
       2) slider_click — main slideshow nav (dots + next-section arrow)
       User-initiated only (delegated on the controls; autoplay never clicks them).
       =========================================================================== */
    function installSliderNavDelegate() {
        document.addEventListener('click', function (e) {
            try {
                var t = e.target;
                if (!t || t.nodeType !== 1 || !t.closest) return;

                var dot = t.closest('carousel-navigation.page-dots button, carousel-navigation.page-dots .tap-area');
                var arrow = t.closest('button[data-action="navigate-next"]');
                if (!dot && !arrow) return;

                var sec = sectionInfo(dot || arrow);
                var navType, slideIndex = null;
                if (dot) {
                    navType = 'dot';
                    var nav = dot.closest('carousel-navigation');
                    if (nav) {
                        var btns = nav.querySelectorAll('button, .tap-area');
                        for (var i = 0; i < btns.length; i++) { if (btns[i] === dot) { slideIndex = i + 1; break; } } // 1-based, matches position/tab_index
                    }
                } else {
                    navType = 'arrow_next';
                }
                emit('slider_click', {
                    nav_type:     navType,
                    slide_index:  slideIndex,
                    section_id:   sec.id,
                    section_type: sec.type
                });
            } catch (err) {}
        }, true);
    }

    /* ===========================================================================
       3) nav_click — header menu + mobile drawer + footer links + social
       =========================================================================== */
    function navContext(t) {
        try {
            // --- Mobile hamburger menu (Prestige <header-sidebar id="sidebar-menu">) ---
            // This drawer renders OUTSIDE <x-header> (overlay layer) and the hamburger is
            // a <button> (not a link/summary), so the <x-header> branch below missed BOTH
            // the open-toggle and every link inside the open menu -- on small screens NO
            // nav_click fired at all. Capture them here, independently of <x-header>.
            // (Selector targets the drawer by id/tag/class since it's <header-sidebar
            // id="sidebar-menu" class="header-sidebar …">, NOT a <menu-drawer>.)
            var mToggle = t.closest('[aria-controls="sidebar-menu"]');
            if (mToggle) {
                return {
                    location: 'header',
                    node: mToggle,
                    href: null,
                    depth: 'mobile',
                    is_parent_toggle: true   // opening/closing the hamburger menu
                };
            }
            var mDrawer = t.closest('#sidebar-menu, header-sidebar, .header-sidebar');
            if (mDrawer) {
                var mNode = t.closest('a[href], summary[data-follow-link], button[data-follow-link]');
                if (!mNode) return null;
                var mIsToggle = mNode.tagName && mNode.tagName.toLowerCase() === 'summary';
                return {
                    location: 'header',
                    node: mNode,
                    href: attr(mNode, 'href') || attr(mNode, 'data-follow-link'),
                    depth: 'mobile',
                    is_parent_toggle: mIsToggle
                };
            }

            // Header: any link/summary inside <x-header> that lives in a nav/menu/drawer
            // context, plus the logo and the cart / search / account icons.
            var header = t.closest('x-header');
            if (header) {
                // THE LOGO (Dorian 2026-09-06: "track clicks on sections - titles,
                // logos"). It is a direct child of <x-header> - `<a class="header__logo">`
                // at sections/header.liquid - inside none of the menu surfaces, so the
                // menu test below dropped it. It was never in .header__secondary-nav,
                // whatever an earlier comment here said.
                var logo = t.closest('.header__logo');
                if (logo) {
                    var lNode = t.closest('a[href]') || logo;
                    return { location: 'header', node: lNode, href: attr(lNode, 'href'), depth: 'logo', is_parent_toggle: false };
                }
                // THE ICONS: cart, search, account. Utility, not a menu - and a
                // click all the same. `depth: 'icon'` keeps them apart from the
                // menu rows in every count that ever grouped on depth.
                var icons = t.closest('.header__secondary-nav');
                if (icons) {
                    var iNode = t.closest('a[href], button, summary[data-follow-link]');
                    if (!iNode) return null;
                    return {
                        location: 'header',
                        node: iNode,
                        href: attr(iNode, 'href') || attr(iNode, 'data-follow-link'),
                        depth: 'icon',
                        is_parent_toggle: false
                    };
                }
                var hNode = t.closest('a[href], summary[data-follow-link]');
                if (!hNode) return null;
                // Allow only real menu surfaces: desktop primary nav, mega-menu, the
                // disclosure summaries, and the mobile menu drawer (<menu-drawer>).
                var inMenu = hNode.closest('.header__primary-nav, .mega-menu, .header__menu-disclosure, menu-drawer, .menu-drawer, [class*="menu-drawer"]');
                if (!inMenu) return null;
                var isToggle = hNode.tagName && hNode.tagName.toLowerCase() === 'summary';
                var depth = hNode.closest('.mega-menu') ? 'submenu'
                          : (hNode.closest('menu-drawer, .menu-drawer, [class*="menu-drawer"]') ? 'mobile' : 'top');
                return {
                    location: 'header',
                    node: hNode,
                    href: attr(hNode, 'href') || attr(hNode, 'data-follow-link'),
                    depth: depth,
                    is_parent_toggle: isToggle
                };
            }
            // Footer: any link inside the footer section.
            var footer = t.closest('.shopify-section--footer, .footer');
            if (footer) {
                var fNode = t.closest('a[href]');
                if (!fNode) return null;
                var isSocial = !!fNode.closest('[class*="social"]');
                return {
                    location: 'footer',
                    node: fNode,
                    href: attr(fNode, 'href'),
                    depth: isSocial ? 'social' : 'link',
                    is_parent_toggle: false
                };
            }
        } catch (e) {}
        return null;
    }
    function installNavClickDelegate() {
        document.addEventListener('click', function (e) {
            try {
                var t = e.target;
                if (!t || t.nodeType !== 1 || !t.closest) return;
                var ctx = navContext(t);
                if (!ctx) return;
                var href = ctx.href;
                emit('nav_click', {
                    location:         ctx.location,
                    label:            text(ctx.node) || attr(ctx.node, 'aria-label'),
                    href:             href,
                    depth:            ctx.depth,
                    is_parent_toggle: ctx.is_parent_toggle,
                    is_external:      isExternal(href)
                });
            } catch (err) {}
        }, true);
    }

    /* ===========================================================================
       4) search_performed — predictive-search header form submit
       =========================================================================== */
    function installSearchListener() {
        document.addEventListener('submit', function (e) {
            try {
                var form = e.target;
                if (!form || !form.closest) return;
                if (!form.matches('#predictive-search-form')) return;
                var input = form.querySelector('input[name="q"], input[type="search"]');
                var q = input ? (input.value || '') : '';
                q = q.replace(/\s+/g, ' ').trim();
                emit('search_performed', {
                    query:  q ? q.slice(0, 200) : null,
                    source: 'header_predictive'
                });
            } catch (err) {}
        }, true);
    }

    /* ===========================================================================
       5) pdp_tab_click — product-info accordion (Details / Sizing / Specs / …)
       The accordion is <details class="accordion" is="accordion-disclosure">. The
       native 'toggle' event from that customized <details> does NOT reliably reach
       a document capture listener, so a toggle-only listener silently missed every
       tab. Primary signal is now the SUMMARY CLICK (always fires + bubbles); the
       'toggle' listener is kept as a backstop and a short dedup window stops the
       two from double-counting the same open/close.
       =========================================================================== */
    var _lastPdpTab = { el: null, at: 0 };
    function _emitPdpTab(d, opened) {
        try {
            if (!d || productHandle() == null) return;              // PDP only
            var now = Date.now();
            if (_lastPdpTab.el === d && (now - _lastPdpTab.at) < 400) return; // dedup click+toggle
            _lastPdpTab.el = d; _lastPdpTab.at = now;
            var summary = d.querySelector('summary');
            emit('pdp_tab_click', {
                tab_label:      text(summary),
                tab_index:      indexWithin(document, 'details.accordion', d),
                opened:         !!opened,
                product_handle: productHandle()
            });
        } catch (e) {}
    }
    function installPdpTabListener() {
        // Primary: click on the accordion summary. opened = the state it's about to
        // flip to (the <details>.open hasn't toggled yet when the click fires).
        document.addEventListener('click', function (e) {
            try {
                var t = e.target;
                if (!t || t.nodeType !== 1 || !t.closest) return;
                var summary = t.closest('summary');
                if (!summary) return;
                var d = summary.closest('details.accordion');
                if (!d) return;
                _emitPdpTab(d, !d.open);
            } catch (err) {}
        }, true);
        // Backstop: native 'toggle' (covers programmatic open/close). Dedup above
        // prevents a double emit when both this and the click fire.
        document.addEventListener('toggle', function (e) {
            try {
                var d = e.target;
                if (!d || d.nodeType !== 1 || !d.matches || !d.matches('details.accordion')) return;
                _emitPdpTab(d, d.open);
            } catch (err) {}
        }, true);
        // DESKTOP "Tabs" section: <x-tabs class="content-tabs"> with <button role="tab">
        // (the Details / Sizing / Specs / Returns / Gov Sales row). These are BUTTONS,
        // not <details>, so the accordion paths above never matched them. Scope to the
        // content-tabs component so the header predictive-search tabs (which also use
        // role="tab") don't false-fire. On mobile this same section renders as
        // details.accordion — already covered by the click path above.
        document.addEventListener('click', function (e) {
            try {
                var t = e.target;
                if (!t || t.nodeType !== 1 || !t.closest) return;
                // Includes Agilite's CUSTOM product-info tabs: <div class="tab-customize">
                // <button class="tablinks-cus" onclick="openTab(...)">Details/Sizing/…</button>
                // (no role="tab"/data-tab — bespoke markup injected per product).
                var TAB_SEL = '[role="tab"], [data-tab], .tabs__title, .tab-title, x-tabs > button, .tablinks-cus, .tab-customize button';
                var tab = t.closest(TAB_SEL);
                if (!tab) return;
                if (tab.closest('x-header, header, predictive-search, .predictive-search')) return; // header search tabs
                if (productHandle() == null) return;  // PDP only
                var list = tab.closest('[role="tablist"], x-tabs, .content-tabs, .tabs, .tab-customize') || document;
                emit('pdp_tab_click', {
                    tab_label:      text(tab) || attr(tab, 'aria-label'),
                    tab_index:      indexWithin(list, TAB_SEL, tab),
                    opened:         true,             // selecting a tab shows its panel
                    product_handle: productHandle()
                });
            } catch (err) {}
        }, true);
    }

    /* ===========================================================================
       6) size_chart_opened — PDP "Size chart" link (aria-controls^="size-chart-")
       =========================================================================== */
    function installSizeChartListener() {
        document.addEventListener('click', function (e) {
            try {
                var t = e.target;
                if (!t || t.nodeType !== 1 || !t.closest) return;
                var btn = t.closest('button[aria-controls^="size-chart-"]');
                if (!btn) return;
                emit('size_chart_opened', {
                    product_handle: productHandle(),
                    modal_id:       attr(btn, 'aria-controls')
                });
            } catch (err) {}
        }, true);
    }

    /* ===========================================================================
       7) video_play — first play of a native <video> (milestone=start)
       'play' does not bubble -> capture phase. YouTube/Vimeo iframes have no
       play event (the home "Watch now" is a link -> home_element_click); they're
       out of scope here. One emit per video element per page.
       =========================================================================== */
    var _videoSeen = (typeof WeakSet === 'function') ? new WeakSet() : null;
    function installVideoListener() {
        document.addEventListener('play', function (e) {
            try {
                var v = e.target;
                if (!v || !v.tagName || v.tagName.toLowerCase() !== 'video') return;
                if (_videoSeen) { if (_videoSeen.has(v)) return; _videoSeen.add(v); }
                var sec = sectionInfo(v);
                var src = v.currentSrc || v.getAttribute('src') || null;
                if (!src) {
                    var s = v.querySelector && v.querySelector('source[src]');
                    if (s) src = s.getAttribute('src');
                }
                emit('video_play', {
                    provider:     'native',
                    milestone:    'start',
                    video_src:    src ? String(src).slice(0, 300) : null,
                    section_id:   sec.id,
                    section_type: sec.type
                });
            } catch (err) {}
        }, true);
        // YouTube FACADE (lite-youtube style): the page renders a <video-cover> /
        // <cued-overlay> placeholder (ytm* classes + an i.ytimg.com thumbnail) and
        // only loads the real iframe when clicked, so the native 'play' above never
        // fires. Emit on the play-button click; pull the video id from the thumbnail
        // background-image (.../vi/<id>/...). One emit per facade.
        document.addEventListener('click', function (e) {
            try {
                var t = e.target;
                if (!t || t.nodeType !== 1 || !t.closest) return;
                var fac = t.closest('video-cover, cued-overlay, [class*="ytmVideoCover"], [class*="ytmCuedOverlay"], .ytmCuedOverlayPlayButton');
                if (!fac) return;
                var host = fac.closest('video-cover, cued-overlay') || fac;
                if (_videoSeen) { if (_videoSeen.has(host)) return; _videoSeen.add(host); }
                var vid = null;
                try {
                    var scope = host.parentNode || document;
                    var thumb = scope.querySelector && scope.querySelector('[style*="ytimg.com/vi/"]');
                    var m = thumb && ((thumb.getAttribute('style') || '').match(/ytimg\.com\/vi\/([^\/"')]+)/));
                    if (m) vid = m[1];
                } catch (e2) {}
                var sec2 = sectionInfo(host);
                emit('video_play', {
                    provider:     'youtube',
                    milestone:    'start',
                    video_id:     vid,
                    video_src:    vid ? ('https://www.youtube.com/watch?v=' + vid) : null,
                    section_id:   sec2.id,
                    section_type: sec2.type
                });
            } catch (err) {}
        }, true);
        // Cross-origin YouTube/Vimeo IFRAME: the player chrome lives INSIDE the
        // iframe (different origin), so clicks on it are invisible to the page and
        // the facade-click path above never sees them. Detect engagement with the
        // focus-steal heuristic: clicking into a video iframe blurs the parent window
        // and makes that iframe document.activeElement. One emit per iframe. This is
        // a "started watching" signal, NOT a true play event (cross-origin can't give
        // that without the YouTube IFrame API + enablejsapi on the embed).
        window.addEventListener('blur', function () {
            try {
                setTimeout(function () {
                    var el = document.activeElement;
                    if (!el || el.tagName !== 'IFRAME') return;
                    var src = el.getAttribute('src') || el.src || '';
                    if (!/youtube|youtu\.be|vimeo/i.test(src)) return;
                    if (_videoSeen) { if (_videoSeen.has(el)) return; _videoSeen.add(el); }
                    var prov = /vimeo/i.test(src) ? 'vimeo' : 'youtube';
                    var m = src.match(/(?:embed\/|youtu\.be\/|[?&]v=)([\w-]{6,})/);
                    var sec3 = sectionInfo(el);
                    emit('video_play', {
                        provider:     prov,
                        milestone:    'start',
                        video_id:     m ? m[1] : null,
                        video_src:    src ? String(src).slice(0, 300) : null,
                        section_id:   sec3.id,
                        section_type: sec3.type,
                        via:          'iframe_focus'   // heuristic: true cross-origin play isn't observable
                    });
                }, 0);
            } catch (e) {}
        }, true);
    }

    /* ===========================================================================
       8) form_submitted — any same-document lead/contact/account form
       Excludes the predictive-search form (-> search_performed), cart/add forms
       (-> add_to_cart), and the localization/country selector.
       =========================================================================== */
    function formName(form) {
        try {
            var n = attr(form, 'data-cae-form-name') || form.id || attr(form, 'name');
            if (n) return n;
            var ft = form.querySelector && form.querySelector('input[name="form_type"]');
            if (ft && ft.value) return ft.value;
            var embed = attr(form, 'data-drip-embedded-form') || attr(form, 'data-drip-id');
            if (embed) return 'drip-' + embed;
            var heading = form.querySelector && form.querySelector('h1,h2,h3,h4,legend');
            var ht = heading ? text(heading) : null;
            if (ht) return ht.slice(0, 80);
            var action = attr(form, 'action') || '';
            var m = action.replace(/[?#].*$/, '').split('/').filter(Boolean);
            if (m.length) return m[m.length - 1];
        } catch (e) {}
        return 'form';
    }
    function installFormSubmitListener() {
        document.addEventListener('submit', function (e) {
            try {
                var form = e.target;
                if (!form || form.tagName !== 'FORM') return;
                if (form.matches('#predictive-search-form')) return; // -> search_performed
                var action = (attr(form, 'action') || '').toLowerCase();
                if (/\/cart\b|\/localization/.test(action)) return; // not a lead form (cart / country selector)
                var ft = form.querySelector && form.querySelector('input[name="form_type"]');
                var ftype = (ft && ft.value) || '';
                // Customer-support / contact form — NOT tracked for now (Dorian, 2026-06-25).
                // Native Shopify support form is form_type='contact' (action contains /contact).
                // Delete this guard to start tracking it later.
                if (ftype === 'contact' || /\/contact\b/.test(action)) return;
                emit('form_submitted', {
                    form_name: formName(form),
                    form_type: ftype || null,
                    action:    attr(form, 'action') || null,
                    page:      pageTypeGuess()
                });
            } catch (err) {}
        }, true);
    }
    function pageTypeGuess() {
        try {
            var p = location.pathname || '';
            if (/^\/products\//.test(p)) return 'product';
            if (/^\/collections\//.test(p)) return 'collection';
            if (/^\/cart/.test(p)) return 'cart';
            if (/^\/pages\//.test(p)) return 'page:' + (p.split('/')[2] || '');
            if (p === '/' || p === '') return 'home';
            return 'other';
        } catch (e) { return 'other'; }
    }

    /* ===========================================================================
       9) review_interaction — Judge.me reviews widget (.jdgm-* injected markup)
       The widget is injected by the Judge.me app (NOT in the theme), so we match
       its stable jdgm-* class names. Only meaningful actions emit (generic clicks /
       reading text inside the widget are ignored). If a button doesn't fire, grab
       its class in the console and add it to the map below.
       =========================================================================== */
    function installReviewListener() {
        document.addEventListener('click', function (e) {
            try {
                var t = e.target;
                if (!t || t.nodeType !== 1 || !t.closest) return;
                var action = null;
                if      (t.closest('.jdgm-write-rev-link, .jdgm-write-review-btn'))      action = 'write_review';
                else if (t.closest('[class*="jdgm-paginate"]'))                          action = 'paginate';
                else if (t.closest('[class*="jdgm-sort"]'))                              action = 'sort';
                else if (t.closest('.jdgm-histogram__row'))                              action = 'filter_star';
                else if (t.closest('[class*="jdgm-rev__thumb"]'))                        action = 'helpful_vote';
                else if (t.closest('.jdgm-prev-badge, .jdgm-preview-badge, .jdgm-star')) action = 'rating_badge';
                else if (t.closest('[class*="jdgm-rev__pic"], .jdgm-rev-photo'))         action = 'review_photo';
                if (!action) return; // ignore generic clicks inside the widget
                emit('review_interaction', {
                    provider:       'judgeme',
                    action:         action,
                    product_handle: productHandle()
                });
            } catch (err) {}
        }, true);
    }

    /* ===========================================================================
       10) review_seen - STOPPED 2026-09-23 (Dorian: "ok", on the proposal to stop
       it). It reported that the Judge.me widget scrolled into view, once a page -
       an impression of the page's own reviews block that no question on any
       screen asked, while review_interaction (what was pressed inside it) keeps
       running. The rows sent before this date stay in the warehouse.
       =========================================================================== */

    /* ===========================================================================
       11) video_play (srcdoc YouTube facade) — the live PDP embed is
       <iframe srcdoc="…<a href='youtube.com/embed/<id>?autoplay=1'>thumb + play</a>…">.
       A srcdoc iframe is SAME-ORIGIN with the page, so we can reach into its
       contentDocument and listen for the REAL click on the facade link — fully
       reliable, no overlay, no enablejsapi, and it never touches playback (the click
       then navigates the iframe to the real cross-origin YouTube embed, which we no
       longer need). Dedupes against the iframe-focus fallback. One emit per video.
       =========================================================================== */
    function installYoutubeFacadeTracking() {
        function hook(ifr) {
            try {
                if (ifr.__caeYtFacade) return;
                var sd = ifr.getAttribute('srcdoc') || '';
                if (!/youtube\.com\/embed\//i.test(sd)) return;     // youtube srcdoc facades only
                ifr.__caeYtFacade = true;
                var idm = sd.match(/youtube\.com\/embed\/([\w-]{6,})/i);
                var vid = idm ? idm[1] : null;
                function attach() {
                    try {
                        var doc = ifr.contentDocument;               // accessible: srcdoc = same-origin
                        if (!doc || !doc.addEventListener) return false;
                        if (doc.__caeBound) return true;
                        doc.__caeBound = true;
                        doc.addEventListener('click', function () {
                            try {
                                if (_videoSeen) { if (_videoSeen.has(ifr)) return; _videoSeen.add(ifr); }
                                emit('video_play', {
                                    provider:  'youtube',
                                    milestone: 'start',
                                    video_id:  vid,
                                    video_src: vid ? ('https://www.youtube.com/watch?v=' + vid) : null,
                                    via:       'srcdoc_facade'
                                });
                            } catch (e) {}
                        }, true);
                        return true;
                    } catch (e) { return false; }   // sandboxed/cross-origin -> blur heuristic covers it
                }
                if (!attach()) {
                    ifr.addEventListener('load', attach);
                    var n = 0;
                    (function retry() { if (n++ < 25 && !attach()) setTimeout(retry, 200); })(); // ~5s for srcdoc to parse
                }
            } catch (e) {}
        }
        function scan() {
            try { var l = document.querySelectorAll('iframe[srcdoc]'); for (var i = 0; i < l.length; i++) hook(l[i]); } catch (e) {}
        }
        scan();
        try { new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true }); } catch (e) {}
    }

    /* ===========================================================================
       12) easy_suggestion_add / easy_suggestion_delete — Easy Suggestion System
       cross-sell cards on the *-easy-suggestion product pages. Mirrors the gear-
       builder add/delete tracking. Each <li class="suggestion-card" data-handle
       data-variant-id> holds an <input class="agb-item-checkbox">; the system fires a
       'change' (including a SYNTHETIC one on whole-card clicks) then stopPropagation()s
       in the TARGET phase — so we listen in the CAPTURE phase to catch it first.
       Scoped to .product-suggestion so it never collides with the gear builder (which
       agb-analytics tracks on its own).
       =========================================================================== */
    function installEasySuggestionTracking() {
        document.addEventListener('change', function (e) {
            try {
                var cb = e.target;
                if (!cb || cb.type !== 'checkbox' || !cb.classList || !cb.classList.contains('agb-item-checkbox')) return;
                var card = cb.closest('.suggestion-card');
                if (!card || !card.closest('.product-suggestion, .suggestions-list')) return; // easy-suggestion only
                emit(cb.checked ? 'easy_suggestion_add' : 'easy_suggestion_delete', {
                    product_handle: attr(card, 'data-handle'),
                    variant_id:     attr(card, 'data-variant-id'),
                    label:          text(card.querySelector('.suggestion-card-title')),
                    source_handle:  productHandle()
                });
            } catch (err) {}
        }, true);
    }

    /* ---- reco_state: one snapshot per page of the recommendation state ------ */
    // The attribution foundation: the OFFLINE recommendation state this page
    // started from (source, list, colour, flow). The session blender's
    // adjustments are the separate reco_adjust event (reco-session.js) - join
    // both on session_id: reco_state = the base, reco_adjust = what the
    // session changed it to. Reads ONLY localStorage/window state that
    // recommendations.js + the colour system already maintain (isolation
    // principle - no upstream edits). Emitted once per page after the fetches
    // settle; one 5s retry when recs haven't landed yet, then whatever state
    // exists is the truth (src:'none' sessions ARE the coverage metric).
    var _recStateSent = false;
    function emitRecState(attempt) {
        if (_recStateSent) return;
        var recs = [];
        try { recs = JSON.parse(localStorage.getItem('recommendedProducts') || '[]') || []; } catch (e) {}
        if (!recs.length && attempt < 1) { setTimeout(function () { emitRecState(attempt + 1); }, 5000); return; }
        _recStateSent = true;
        var handles = [];
        for (var i = 0; i < recs.length; i++) {
            var h = recs[i] && recs[i].productLink;
            if (h) handles.push(String(h));
        }
        var joined = handles.join('|');
        var hash = 5381;                                   // djb2 over the ordered list
        for (var j = 0; j < joined.length; j++) hash = ((hash << 5) + hash + joined.charCodeAt(j)) | 0;
        var age = null;
        try {
            var t = parseInt(localStorage.getItem('recommendationFetchTime') || '', 10);
            if (t > 0) age = Math.round((Date.now() - t) / 1000);
        } catch (e) {}
        var colour = null;
        try {
            // the served colour: the window global is what color-pref.js
            // applied THIS page; the localStorage cache keeps the service
            // answer under .answer (not at the top level)
            var w = window.SE_colorPref;
            if (w && w.colors && w.colors.length) colour = String(w.colors[0]);
            if (!colour) {
                var cp = JSON.parse(localStorage.getItem('SE_colorPref') || 'null');
                var cols = cp && cp.answer && cp.answer.colors;
                if (cols && cols.length) colour = String(cols[0]);
            }
        } catch (e) {}
        var src = 'unknown';
        try { src = localStorage.getItem('recommendedSource') || (handles.length ? 'unknown' : 'none'); } catch (e) {}
        if (!handles.length) src = 'none';
        emit('reco_state', {
            src: src,
            n: handles.length,
            top: joined.slice(0, 400),
            list_hash: ('00000000' + (hash >>> 0).toString(16)).slice(-8),
            fetch_age_s: age,
            colour: colour,
            flow: (typeof window.__userFlow === 'string' ? window.__userFlow : null)
        });
    }

    /* ---- boot ------------------------------------------------------------- */
    function start() {
        installHomeClickDelegate();
        installSliderNavDelegate();
        installNavClickDelegate();
        installSearchListener();
        installPdpTabListener();
        installSizeChartListener();
        installVideoListener();
        installFormSubmitListener();
        installReviewListener();
        installYoutubeFacadeTracking();
        installEasySuggestionTracking();
        _waitForCae(0); // flush anything captured before CAEDev was ready
        setTimeout(function () { emitRecState(0); }, 3000); // after fetches settle
    }
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start);
    } else {
        start();
    }
})();
